import moment, { Moment } from 'moment';
import { HdPlanetKey } from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { HD_EPHEMERIS_DOMAIN_MAX_MS, HD_EPHEMERIS_DOMAIN_MIN_MS } from '../ephemeris-lanes/hd-ephemeris-lanes.utils';
import { HdTransitSegment } from '../transit.models';

/**
 * Rave New Year: het moment waarop de zon poort 41 binnengaat (lijn 1, ±22
 * januari), de start van het Rave-jaar. Pure helpers; het ophalen van de
 * zon-segmenten doet {@link HdRaveNewYearService}.
 */

/** De poort waarmee het Rave-jaar opent. */
export const HD_RAVE_NEW_YEAR_GATE = 41;

/** Jaren binnen het ephemeris-domein van de transits (1800-01-01 tot 2400-01-01). */
export const HD_RAVE_NEW_YEAR_MIN_YEAR = moment.utc(HD_EPHEMERIS_DOMAIN_MIN_MS).year();
export const HD_RAVE_NEW_YEAR_MAX_YEAR = moment.utc(HD_EPHEMERIS_DOMAIN_MAX_MS).year() - 1;

/**
 * Zoekvenster (UTC) voor de intrede in een jaar. De zon staat elk jaar tussen
 * ±19 en ±22 januari op het begin van poort 41; het venster laat ruim marge.
 */
export function hdRaveNewYearSearchWindow(year: number): { readonly fromUtc: Moment; readonly toUtc: Moment } {
  return {
    fromUtc: moment.utc({ year, month: 0, date: 10 }),
    toUtc: moment.utc({ year, month: 1, date: 3 }),
  };
}

/**
 * Is dit segment de intrede van de zon in poort 41? Lijn 1 als de lijn bekend
 * is (lijn-granulariteit), en de vorige stand ligt in een andere poort. Zonder
 * vorige stand (rand van de geladen data) telt het begin van 41.1 ook.
 */
export function isHdRaveNewYearIngress(
  planetKey: HdPlanetKey,
  segment: HdTransitSegment,
  previous: HdTransitSegment | undefined,
): boolean {
  if (planetKey !== 'sun' || segment.gate !== HD_RAVE_NEW_YEAR_GATE) return false;
  if (segment.line != null && segment.line !== 1) return false;
  return previous == null || previous.gate !== HD_RAVE_NEW_YEAR_GATE;
}

/** Het Rave-jaar waarmee een intrede op `atMs` opent (de intrede valt in januari). */
export function hdRaveNewYearOf(atMs: number): number {
  return moment.utc(atMs).year();
}

/** Tijdstip (UTC) van de intrede in de zon-segmenten, of `null` als ze er niet in staat. */
export function findHdRaveNewYearMoment(sunSegments: readonly HdTransitSegment[]): Moment | null {
  for (let index = 0; index < sunSegments.length; index++) {
    const segment = sunSegments[index];
    if (isHdRaveNewYearIngress('sun', segment, sunSegments[index - 1])) return moment.utc(segment.fromUtc);
  }
  return null;
}

/**
 * Tijdstip waarop de Rave New Year-chart berekend wordt: de eerste hele seconde
 * minstens één seconde ná de gevonden intrede.
 *
 * BELANGRIJK: niet de intrede zelf. Die komt uit een bisectie tot op 1 s
 * (`TransitSegmentSolver`: het midden van het bracket, dus tot 0,5 s naast de
 * echte grens), en de backend rekent de chart op `birthData.time.utc`, dat
 * `BirthDataBuilder` op hele seconden afkapt. Samen lag de chart tot 1,5 s vóór
 * de grens en stond de zon nog op 60.6 (Steven 2026-10-25, Rave New Year 2027).
 * Een hele seconde verandert niet meer door het afkappen, en de extra seconde
 * ligt voorbij elke bisectiefout. Weghalen brengt die fout terug; zie de spec
 * "rekent de chart op de eerste hele seconde ná de intrede".
 */
export function hdRaveNewYearChartMoment(ingress: Moment): Moment {
  return moment.utc(Math.ceil(ingress.valueOf() / 1000) * 1000 + 1000);
}

/** Een geldig jaar binnen het domein (afgerond en begrensd). */
export function clampHdRaveNewYear(year: number): number {
  const rounded = Math.round(year);
  return Math.min(HD_RAVE_NEW_YEAR_MAX_YEAR, Math.max(HD_RAVE_NEW_YEAR_MIN_YEAR, rounded));
}
